import type { DateFormatOrder } from './preferences';
import { dateFormatPattern } from './preferences';

export type ParseDateResult =
    | { ok: true; ymd: string }
    | { ok: false; error: string; ymd?: undefined };

function pad2(n: number): string {
    return String(n).padStart(2, '0');
}

function isValidYmd(y: number, m: number, d: number): boolean {
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return false;
    if (y < 1000 || y > 9999 || m < 1 || m > 12 || d < 1 || d > 31) return false;
    const dt = new Date(y, m - 1, d);
    return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
}

function toYmd(y: number, m: number, d: number): string {
    return `${y}-${pad2(m)}-${pad2(d)}`;
}

/**
 * True when typed text has a 4-digit year (or is already yyyy-MM-dd).
 * Incomplete years like "24/08/2" or "24/08/20" are still being typed —
 * committing them as 2002 / 2020 fights both the input and autosave.
 */
export function typedDateLooksComplete(text: string, order: DateFormatOrder): boolean {
    const raw = String(text || '').trim();
    if (!raw) return false;
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return true;

    const normalized = raw.replace(/[.\s]+/g, '/').replace(/-/g, '/');
    const parts = normalized.split('/').map((p) => p.trim()).filter(Boolean);
    if (parts.length !== 3) return false;

    const yearPart = order === 'YMD' ? parts[0] : parts[2];
    return /^\d{4}$/.test(yearPart || '');
}

/**
 * Parse user-typed calendar text into yyyy-MM-dd using the preferred order.
 * Also accepts ISO yyyy-MM-dd. Rejects impossible dates (e.g. 31/07 under MDY).
 */
export function parseDateInput(text: string, order: DateFormatOrder): ParseDateResult {
    const raw = String(text || '').trim();
    if (!raw) {
        return { ok: false, error: 'Enter a date.' };
    }

    // Already storage form
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        const [ys, ms, ds] = raw.split('-');
        const y = Number(ys);
        const m = Number(ms);
        const d = Number(ds);
        if (!isValidYmd(y, m, d)) {
            return { ok: false, error: 'That date is not valid.' };
        }
        return { ok: true, ymd: toYmd(y, m, d) };
    }

    // Normalize separators
    const normalized = raw.replace(/[.\s]+/g, '/').replace(/-/g, '/');
    const parts = normalized.split('/').map((p) => p.trim()).filter(Boolean);
    if (parts.length !== 3) {
        return {
            ok: false,
            error: `Enter a date as ${dateFormatPattern(order)}.`,
        };
    }

    const nums = parts.map((p) => Number(p));
    if (nums.some((n) => !Number.isFinite(n))) {
        return {
            ok: false,
            error: `Enter a date as ${dateFormatPattern(order)}.`,
        };
    }

    let y: number;
    let m: number;
    let d: number;
    switch (order) {
        case 'DMY':
            [d, m, y] = nums as [number, number, number];
            break;
        case 'YMD':
            [y, m, d] = nums as [number, number, number];
            break;
        case 'MDY':
        default:
            [m, d, y] = nums as [number, number, number];
            break;
    }

    // Two-digit year → 2000–2099
    if (y >= 0 && y < 100) {
        y += 2000;
    }

    if (!isValidYmd(y, m, d)) {
        return {
            ok: false,
            error: `Enter a date as ${dateFormatPattern(order)}.`,
        };
    }

    return { ok: true, ymd: toYmd(y, m, d) };
}
